/**
 * Completion support for editors: the suggestable unit vocabulary, and analysis
 * of a `~{ … }` region to surface the custom dimensions and tags already in
 * scope (so the LSP can offer context-aware calc completion).
 */
import { evalExpr } from "./engine.js";
import { parseExpression } from "./parser.js";
import { baseOf, tagOf, nameOf } from "./units.js";

export interface UnitInfo {
  readonly label: string;
  readonly detail: string;
  readonly category: "data" | "time" | "money";
}

/** Every built-in unit token worth completing (magnitudes glue to numbers, so omitted). */
export const CALC_UNITS: readonly UnitInfo[] = [
  { label: "B", detail: "byte", category: "data" },
  { label: "KB", detail: "kilobyte · 1e3 B", category: "data" },
  { label: "MB", detail: "megabyte · 1e6 B", category: "data" },
  { label: "GB", detail: "gigabyte · 1e9 B", category: "data" },
  { label: "TB", detail: "terabyte · 1e12 B", category: "data" },
  { label: "PB", detail: "petabyte · 1e15 B", category: "data" },
  { label: "bit", detail: "bit · B/8", category: "data" },
  { label: "Kbit", detail: "kilobit", category: "data" },
  { label: "Mbit", detail: "megabit", category: "data" },
  { label: "Gbit", detail: "gigabit", category: "data" },
  { label: "ms", detail: "millisecond", category: "time" },
  { label: "s", detail: "second", category: "time" },
  { label: "min", detail: "minute", category: "time" },
  { label: "h", detail: "hour", category: "time" },
  { label: "d", detail: "day", category: "time" },
  { label: "wk", detail: "week", category: "time" },
  { label: "mo", detail: "month · 30 d", category: "time" },
  { label: "y", detail: "year · 365 d", category: "time" },
  { label: "$", detail: "money", category: "money" },
];

/**
 * Scan a `~{ … }` region body for the custom dimensions and tags it already
 * mentions, so completion can suggest reusing them (`img`, `user`, `#md`). Lines
 * that don't parse (prose, or a half-typed line) are skipped.
 */
export function analyzeBlock(text: string): { dims: string[]; tags: string[] } {
  const dims = new Set<string>();
  const tags = new Set<string>();
  for (const rawLine of text.split("\n")) {
    const code = rawLine.split("--")[0] ?? "";
    for (const part of code.split(/[=,]/)) {
      const s = part.trim();
      if (s === "") continue;
      try {
        const q = evalExpr(parseExpression(s));
        for (const k of q.dims.keys()) {
          const t = tagOf(k);
          if (t) tags.add(t);
          if (baseOf(k) === "custom") dims.add(nameOf(k));
        }
      } catch {
        /* prose / partial line */
      }
    }
  }
  return { dims: [...dims].sort(), tags: [...tags].sort() };
}
